'use client';

import { generateFaqListJsonLd } from '@/app/_jsonLd/jsonLdFaq';
import { StoryblokReactComponent } from '@/storyblok/components/componentsMap';
import { PostFaqItemStoryblok, PostFaqStoryblok } from '@/storyblok/types';
import { makeRichTextRenderOptions, richTextToString } from '@/storyblok/utils/richText';
import { monaFont } from '@/styles/fonts';
import { ChevronUpIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { render } from 'storyblok-rich-text-react-renderer';

export const PostFAQItem: StoryblokReactComponent<PostFaqItemStoryblok> = ({ story, component }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="border-b border-primary/15 last:border-b-0">
      <button
        onClick={() => setIsOpen(prevState => !prevState)}
        className="flex w-full items-center justify-between py-4 text-sm font-semibold text-neutral-darker sm:py-5 sm:text-base"
      >
        <h3>{component.question}</h3>
        <ChevronUpIcon className={clsx('size-4 stroke-2 transition-transform', isOpen ? 'rotate-0' : '-rotate-180')} />
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all ease-in-out"
        style={{ maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}
      >
        <div className="pb-5 pt-0 text-sm leading-relaxed text-neutral-lighter">
          {render(component.answer, makeRichTextRenderOptions(story))}
        </div>
      </div>
    </div>
  );
};

export const PostFAQ: StoryblokReactComponent<PostFaqStoryblok> = ({ story, component }) => {
  const items = component.items ?? [];
  const jsonLdData = items
    .filter(faq => faq.question && faq.answer)
    .map(faq => ({
      question: faq.question!,
      answer: richTextToString(faq.answer!),
    }));

  return (
    <>
      <script
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFaqListJsonLd(jsonLdData)),
        }}
        type="application/ld+json"
      />

      <div className="mb:gap-8 relative flex flex-col gap-6 overflow-hidden rounded-2xl border border-primary/15 bg-primary-active/5 p-5 sm:p-7 md:p-9">
        <div aria-hidden="true" className="pointer-events-none absolute right-0 top-0 opacity-5">
          <QuestionMarkCircleIcon className="size-44 stroke-1 text-primary sm:size-60 md:size-72" />
        </div>

        <div className="relative">
          <div className={clsx(monaFont, 'mb-3 text-sm font-medium uppercase tracking-widest text-neutral-lighter lg:text-base')}>
            Domande frequenti
          </div>
          <h2 className="text-2xl !font-bold leading-tight tracking-tight text-primary lg:text-4xl">{component.title}</h2>
        </div>

        <div className="relative w-full" data-orientation="vertical">
          {items.map(item => (
            <PostFAQItem key={item._uid} story={story} component={item} />
          ))}
        </div>
      </div>
    </>
  );
};
